import {dropbox_os} from '@dropbox/api-v2-client';
import {useQuery} from '@tanstack/react-query';
import {DropboxClientContext} from 'App';
import {useContext} from 'react';
import {appsQueryKey, listApps} from 'views/apps/api';

/** Upper bound on catalog pages scanned to resolve pins (50 apps per page). */
const MAX_PAGES = 20;

/**
 * Resolve pinned slugs to catalog entries, in pin order. The catalog has no
 * lookup-by-slug route, so page through `list_apps` until every pin is found.
 * Slugs that never resolve (retired, or no longer visible to the viewer) are
 * dropped from the result.
 */
export const usePinnedAppsCatalog = (slugs: string[]) => {
  const client = useContext(DropboxClientContext);

  return useQuery({
    // The authenticated client is stable application context, not catalog identity.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [...appsQueryKey, 'pinned', slugs],
    enabled: client !== null && slugs.length > 0,
    queryFn: async () => {
      if (!client) {
        throw new Error('Dropbox client is not ready');
      }
      const wanted = new Set(slugs);
      const found = new Map<string, dropbox_os.PublishedApp>();
      let cursor: string | undefined;
      for (let page = 0; page < MAX_PAGES && found.size < wanted.size; page++) {
        const response = await listApps(client, cursor);
        (response.apps ?? []).forEach((app) => {
          if (app.slug && wanted.has(app.slug)) {
            found.set(app.slug, app);
          }
        });
        cursor = response.next_page_token || undefined;
        if (!cursor) {
          break;
        }
      }
      return slugs.flatMap((slug) => {
        const app = found.get(slug);
        return app ? [app] : [];
      });
    },
  });
};
